import React from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';

/**
 * Floating reminder for postponed events. Sits in the bottom-right corner
 * (above the mobile bottom-nav) whenever the player has at least one
 * deferred vehicle-purchase opportunity. Clicking it brings the oldest
 * postponed event back to the head of `pendingEvents` so the blocking
 * EventPopup re-opens with the same payload.
 *
 * Added 28/05/2026 in response to user feedback: forcing a yes/no on a
 * vehicle offer without letting the player check the fleet feels unfair.
 */
export default function PostponedEventsBadge() {
  const postponedEvents = useGameStore(s => s.postponedEvents || EMPTY_ARRAY);
  const resumePostponedEvent = useGameStore(s => s.resumePostponedEvent);
  const pendingCount = useGameStore(s => (s.pendingEvents || []).length);

  // Hide the badge while the EventPopup is open — otherwise the badge sits
  // on top of the modal which is visually noisy and could be misread as a
  // duplicate prompt.
  if (postponedEvents.length === 0 || pendingCount > 0) return null;

  const next = postponedEvents[0];
  const label = next.name || 'Décision en attente';

  return (
    <div
      className="fixed z-40 bottom-20 right-4 md:bottom-4 md:right-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
      data-testid="postponed-events-badge"
    >
      <button
        onClick={() => resumePostponedEvent(next.instanceId)}
        className="group flex items-center gap-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-full pl-3 pr-4 py-2.5 shadow-lg shadow-amber-900/30 border border-amber-400/60 transition-all"
        title={`Reprendre : ${label}`}
      >
        <div className="relative">
          <Clock className="w-5 h-5" />
          {postponedEvents.length > 1 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-600 border border-white text-[10px] font-bold flex items-center justify-center">
              {postponedEvents.length}
            </span>
          )}
        </div>
        <div className="text-left">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-90 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Décision en attente
          </p>
          <p className="text-xs font-semibold max-w-[180px] truncate">{label}</p>
        </div>
      </button>
    </div>
  );
}
